// La cloche, et les notifications système.
//
// ── Deux choses distinctes, souvent confondues ──────────────────────────────
//
// La CLOCHE vit dans la page : elle marche toujours, sans permission ni
// installation, et c'est elle qui porte l'information.
//
// La notification SYSTÈME est un affichage du téléphone, en plus. Elle demande
// une permission, elle peut être refusée, et elle ne se déclenche ici que
// pendant que l'application est ouverte — c'est l'API Notification, pas Web
// Push : rien n'arrive téléphone rangé. Le réglage vit donc en local, comme la
// permission dont il dépend : accepter les notifications sur son téléphone ne
// dit rien de ce qu'on veut sur l'ordinateur du bureau.
//
// ── Le rythme ────────────────────────────────────────────────────────────────
//
// Trente secondes, et une relève immédiate au retour sur l'écran. Le second
// point compte plus que le premier : on sort son téléphone pour REGARDER, et
// c'est à cet instant que l'information doit être fraîche. Rien n'est relevé
// tant que l'onglet est caché — un téléphone en poche n'a aucune raison de
// parler au serveur.

import {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { api } from './api.ts';
import { useAuth } from './auth.tsx';

/** Les genres de notification que le serveur sait emettre.
 *
 *  La liste doit suivre l'ENUM `notification_kind` de la base : un genre connu
 *  du serveur mais absent d'ici passe quand meme a l'ecran — l'icone retombe
 *  sur la cloche — mais le compilateur cesse d'aider a n'en oublier aucun. */
export type NotificationKind =
    | 'friend_added'
    | 'putting_invite'
    | 'registration_pending'
    | 'round_invite'
    | 'round_finished'
    | 'handicap_improved';

export interface AppNotification {
    id: string;
    kind: NotificationKind;
    title: string;
    body: string | null;
    link: string | null;
    payload: Record<string, unknown>;
    readAt: string | null;
    createdAt: string;
}

interface NotificationsValue {
    notifications: AppNotification[];
    unread: number;
    refresh: () => Promise<void>;
    markRead: (id?: string) => Promise<void>;
    clear: () => Promise<void>;
    /** Réglage local : afficher aussi les notifications du système. */
    nativeEnabled: boolean;
    setNativeEnabled: (v: boolean) => Promise<void>;
    nativeSupported: boolean;
    nativePermission: NotificationPermission | 'unsupported';
    /** Cet appareil est abonné : les bannières arrivent application fermée. */
    pushActive: boolean;
    pushSupported: boolean;
    /** Envoi d'essai vers les appareils abonnés de ce compte. */
    testPush: () => Promise<{ sent: number; devices: number }>;
    /** La dernière arrivée, tant qu'elle n'est pas écartée. Nourrit la bulle
     *  affichée par-dessus l'écran en cours. */
    toast: AppNotification | null;
    dismissToast: () => void;
}

const Ctx = createContext<NotificationsValue>({
    notifications: [], unread: 0,
    refresh: async () => {}, markRead: async () => {}, clear: async () => {},
    nativeEnabled: false, setNativeEnabled: async () => {},
    nativeSupported: false, nativePermission: 'unsupported',
    pushActive: false, pushSupported: false,
    testPush: async () => ({ sent: 0, devices: 0 }),
    toast: null, dismissToast: () => {},
});

/** Flux rompu : c'est la relève qui fait tout le travail. */
const POLL_MS = 30_000;
/** Flux ouvert : simple filet contre un évènement manqué pendant une coupure
 *  de la connexion d'écoute côté serveur. */
const POLL_SLOW_MS = 120_000;
const STORAGE_KEY = 'golf.notifications.native';

export const nativeSupported = (): boolean =>
    typeof window !== 'undefined' && 'Notification' in window;

/** Web Push demande un service worker ET l'API PushManager. Absent en
 *  navigation privée sur certains navigateurs, et sur iOS tant que
 *  l'application n'est pas installée sur l'écran d'accueil. */
export const pushSupported = (): boolean =>
    typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && typeof window !== 'undefined' && 'PushManager' in window;

const listNotifications = () =>
    api<{ notifications: AppNotification[]; unread: number }>('/notifications');

/** base64url → octets, format exigé par `applicationServerKey`.
 *
 *  Typé `Uint8Array<ArrayBuffer>` et non `Uint8Array` : depuis TypeScript 5.7
 *  le second admet un SharedArrayBuffer, que les API du navigateur refusent. */
function urlBase64ToBytes(s: string): Uint8Array<ArrayBuffer> {
    const padded = (s + '='.repeat((4 - (s.length % 4)) % 4))
        .replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(padded);
    const out = new Uint8Array(new ArrayBuffer(raw.length));
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

/** Abonne cet appareil et déclare l'abonnement au serveur.
 *
 *  Réutilise l'abonnement existant quand il y en a un : `subscribe()` sur un
 *  enregistrement déjà abonné échoue si la clé serveur diffère, et en produit
 *  un doublon sinon. */
async function subscribePush(): Promise<boolean> {
    if (!pushSupported()) return false;
    try {
        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        const { publicKey } = await api<{ publicKey: string }>('/notifications/push/key');

        const sub = existing ?? await reg.pushManager.subscribe({
            // Toujours vrai, et imposé par les navigateurs : un abonnement
            // silencieux servirait à pister sans rien montrer.
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToBytes(publicKey),
        });

        const json = sub.toJSON() as { endpoint?: string; keys?: Record<string, string> };
        if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return false;

        await api('/notifications/push/subscribe', {
            method: 'POST',
            body: { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } },
        });
        return true;
    } catch {
        // Service de poussée injoignable, permission retirée entre-temps,
        // navigateur sans support : les bannières « application ouverte »
        // continuent de fonctionner, et l'écran le dit.
        return false;
    }
}

async function unsubscribePush(): Promise<void> {
    if (!pushSupported()) return;
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!sub) return;
        // Le serveur d'abord : si le désabonnement local réussit et que
        // l'appel échoue, la ligne resterait et l'on pousserait vers un
        // endpoint mort jusqu'au premier 410.
        await api('/notifications/push/subscribe', {
            method: 'DELETE', body: { endpoint: sub.endpoint },
        }).catch(() => {});
        await sub.unsubscribe();
    } catch { /* rien à défaire */ }
}

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [unread, setUnread] = useState(0);
    const [nativeEnabled, setNative] = useState(
        () => typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY) === '1',
    );
    const [streamOpen, setStreamOpen] = useState(false);
    const [pushActive, setPushActive] = useState(false);

    // Ce qui a déjà été signalé au système. Sans ce garde, chaque relève
    // rejouerait la même notification toutes les trente secondes.
    const [toast, setToast] = useState<AppNotification | null>(null);
    const dismissToast = useCallback(() => setToast(null), []);
    const announced = useRef<Set<string>>(new Set());
    const primed = useRef(false);

    /** Applique un état de cloche, d'où qu'il vienne — relève ou flux poussé.
     *
     *  Un seul chemin : c'est ici que se décide ce qui mérite une bannière du
     *  système, et le dédoublonner entre les deux sources aurait fini par les
     *  faire diverger. */
    const applyResult = useCallback((r: { notifications: AppNotification[]; unread: number }) => {
        setNotifications(r.notifications);
        setUnread(r.unread);

        const fresh = r.notifications.filter((n) => !n.readAt && !announced.current.has(n.id));
        for (const n of r.notifications) announced.current.add(n.id);

        // Le tout premier état ne notifie pas : à l'ouverture de l'application,
        // tout le non-lu est « nouveau », et le téléphone se mettrait à empiler
        // des bannières pour des faits parfois vieux de plusieurs jours.
        if (!primed.current) { primed.current = true; return; }

        // Une seule bannière par évènement, jamais deux.
        //
        // Quand l'appareil est abonné, c'est le service worker qui affiche, et
        // lui affiche toujours. La page ne prend le relais que SANS
        // abonnement, et seulement écran caché : application ouverte, c'est la
        // bulle qui parle, et empiler une bannière par-dessus ce qui est déjà
        // sous les yeux est le meilleur moyen de faire couper les
        // notifications.
        // La bulle dans l'application, quoi qu'il arrive par ailleurs.
        //
        // Elle n'est pas un doublon de la bannière système : celle-ci apparaît
        // hors de l'application, en haut de l'écran, et disparaît sous le doigt
        // quand on est en train de jouer. La bulle, elle, est DANS l'écran en
        // cours — c'est le seul endroit où l'œil est déjà posé.
        //
        // La plus récente seulement : deux invitations en même temps se
        // succèdent dans la cloche, elles n'ont pas à se disputer l'écran.
        if (fresh[0]) setToast(fresh[0]);

        const shouldAnnounce = nativeEnabled
            && nativeSupported()
            && Notification.permission === 'granted'
            && !pushActive
            && document.visibilityState === 'hidden';

        if (shouldAnnounce) {
            // Trois au maximum : au-delà, on empile des bannières que personne
            // ne lit, et la cloche fait déjà le compte.
            for (const n of fresh.slice(0, 3)) {
                try {
                    new Notification(n.title, {
                        body: n.body ?? undefined,
                        icon: '/icon.svg',
                        // Une même source ne s'affiche qu'une fois : le tag
                        // remplace la bannière précédente au lieu de l'empiler.
                        tag: n.id,
                    });
                } catch {
                    // Certains navigateurs refusent hors service worker.
                }
            }
        }
    }, [nativeEnabled, pushActive]);

    const refresh = useCallback(async (): Promise<void> => {
        if (!user) { setNotifications([]); setUnread(0); return; }
        try {
            applyResult(await listNotifications());
        } catch {
            // Une relève ratée ne doit rien casser : on garde l'état
            // précédent. Le réseau d'un practice est mauvais par nature.
        }
    }, [user, applyResult]);

    // Flux poussé : une invitation arrive quand elle est lancée.
    //
    // On invite quelqu'un qui est là, souvent à côté ; une demi-minute
    // d'attente se remarque immédiatement. Le flux se refait tout seul sur
    // coupure réseau ; il abandonne en revanche sur une réponse non conforme —
    // un jeton expiré — et c'est alors la relève qui reprend, en rafraîchissant
    // la session au passage.
    useEffect(() => {
        if (!user) return undefined;
        const es = new EventSource('/api/notifications/stream');
        es.addEventListener('open', () => setStreamOpen(true));
        es.addEventListener('notifications', (ev) => {
            try {
                applyResult(JSON.parse((ev as MessageEvent<string>).data) as {
                    notifications: AppNotification[]; unread: number;
                });
                setStreamOpen(true);
            } catch { /* trame tronquée : la relève rattrape */ }
        });
        es.addEventListener('error', () => setStreamOpen(false));
        return () => es.close();
    }, [user, applyResult]);

    // Relève de secours. Lente quand le flux tient — simple filet contre un
    // évènement manqué — et immédiate au retour sur l'écran.
    useEffect(() => {
        if (!user) {
            setNotifications([]); setUnread(0);
            announced.current.clear(); primed.current = false; setToast(null);
            return undefined;
        }

        const period = streamOpen ? POLL_SLOW_MS : POLL_MS;
        let timer: number | undefined;
        const tick = (): void => {
            if (document.visibilityState === 'visible') void refresh();
            timer = window.setTimeout(tick, period);
        };
        tick();

        const onVisible = (): void => {
            if (document.visibilityState === 'visible') void refresh();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);

        return () => {
            if (timer) window.clearTimeout(timer);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [user, refresh, streamOpen]);

    const markRead = useCallback(async (id?: string): Promise<void> => {
        // Optimiste : la cloche doit s'éteindre au doigt, pas au retour du
        // serveur. La relève suivante corrigera si l'appel a échoué.
        setNotifications((list) => list.map(
            (n) => (id && n.id !== id ? n : { ...n, readAt: n.readAt ?? new Date().toISOString() }),
        ));
        setUnread((u) => (id ? Math.max(0, u - 1) : 0));
        await api('/notifications/read', { method: 'POST', body: { id } }).catch(() => {});
    }, []);

    const clear = useCallback(async (): Promise<void> => {
        setNotifications([]); setUnread(0);
        await api('/notifications', { method: 'DELETE' }).catch(() => {});
    }, []);

    /** Activer demande la permission, puis abonne CET appareil à Web Push.
     *
     *  Les deux vont ensemble et une seule question est posée au joueur : la
     *  permission des notifications est aussi celle de la poussée, et lui
     *  demander deux fois la même chose sous deux formes différentes
     *  n'apporterait qu'un refus de plus.
     *
     *  Refuser est définitif — le navigateur s'en souvient, l'application ne
     *  peut pas revenir dessus — et le réglage retombe.
     *
     *  L'abonnement peut échouer là où la permission passe : navigateur sans
     *  service worker, iOS hors écran d'accueil, service de poussée
     *  injoignable. Ce n'est pas bloquant : les bannières « application
     *  ouverte » fonctionnent quand même, et c'est ce que dit l'écran. */
    const setNativeEnabled = useCallback(async (v: boolean): Promise<void> => {
        if (!v) {
            localStorage.setItem(STORAGE_KEY, '0');
            setNative(false);
            setPushActive(false);
            await unsubscribePush();
            return;
        }
        if (!nativeSupported()) return;
        const perm = Notification.permission === 'granted'
            ? 'granted'
            : await Notification.requestPermission();
        const ok = perm === 'granted';
        localStorage.setItem(STORAGE_KEY, ok ? '1' : '0');
        setNative(ok);
        if (ok) setPushActive(await subscribePush());
    }, []);

    // Réabonnement silencieux au démarrage.
    //
    // Un abonnement expire : le navigateur en change au gré de ses mises à
    // jour, et le service de poussée peut le révoquer. Sans cette reprise, les
    // notifications cesseraient un jour sans que personne ne comprenne
    // pourquoi, l'interrupteur restant obstinément allumé.
    useEffect(() => {
        if (!user || !nativeEnabled) { setPushActive(false); return; }
        if (!nativeSupported() || Notification.permission !== 'granted') return;
        void subscribePush().then(setPushActive);
    }, [user, nativeEnabled]);

    const testPush = useCallback(
        () => api<{ sent: number; devices: number }>(
            '/notifications/push/test', { method: 'POST' },
        ),
        [],
    );

    const value = useMemo<NotificationsValue>(() => ({
        notifications, unread, refresh, markRead, clear,
        nativeEnabled, setNativeEnabled,
        nativeSupported: nativeSupported(),
        nativePermission: nativeSupported() ? Notification.permission : 'unsupported',
        pushActive, pushSupported: pushSupported(), testPush,
        toast, dismissToast,
    }), [
        notifications, unread, refresh, markRead, clear,
        nativeEnabled, setNativeEnabled, pushActive, testPush,
        toast, dismissToast,
    ]);

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useNotifications = (): NotificationsValue => useContext(Ctx);
